/* The message list, virtualised: only the rows in view exist, so a mailbox of
 * a hundred thousand messages is a few dozen elements. Rows are clones of the
 * page's `row-template`, positioned inside a spacer as tall as the whole list.
 */

import { shortDate } from "./format.js";
import { setMarkedText } from "./highlight.js";

export class MessageList {
  /**
   * @param {HTMLElement} scroller  the `data-mb="list"` element
   * @param {HTMLTemplateElement} template
   */
  constructor(scroller, template) {
    this.scroller = scroller;
    this.spacer = scroller.querySelector('[data-mb="spacer"]');
    this.template = template;
    this.rows = new Map(); // position -> element
    this.mailbox = null;

    scroller.addEventListener("scroll", () => this.draw(), { passive: true });
    new ResizeObserver(() => this.draw()).observe(scroller);
  }

  get rowHeight() {
    // Read from the stylesheet's `--mb-row`, so the height lives in one place.
    const probe = parseFloat(getComputedStyle(this.scroller).getPropertyValue("--mb-row"));
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return (probe || 4) * rem;
  }

  show(mailbox) {
    if (this.mailbox !== mailbox) {
      this.mailbox = mailbox;
      for (const row of this.rows.values()) row.remove();
      this.rows.clear();
      // Height first, then the mailbox's own remembered scroll position, and
      // only then draw: drawing saves the scroll position, and drawing before
      // restoring would save the previous mailbox's into this one.
      const ids = mailbox ? mailbox.listed() : [];
      this.spacer.style.height = `${ids.length * this.rowHeight}px`;
      this.scroller.scrollTop = mailbox?.scrollTop ?? 0;
    }
    this.draw();
  }

  draw() {
    const mailbox = this.mailbox;
    const ids = mailbox ? mailbox.listed() : [];
    const height = this.rowHeight;

    this.spacer.style.height = `${ids.length * height}px`;

    const first = Math.max(0, Math.floor(this.scroller.scrollTop / height) - 6);
    const last = Math.min(ids.length, Math.ceil((this.scroller.scrollTop + this.scroller.clientHeight) / height) + 6);

    for (const [position, row] of this.rows) {
      if (position < first || position >= last) {
        row.remove();
        this.rows.delete(position);
      }
    }

    for (let position = first; position < last; position++) {
      let row = this.rows.get(position);

      if (!row) {
        row = this.template.content.firstElementChild.cloneNode(true);
        row.style.top = `${position * height}px`;
        this.spacer.appendChild(row);
        this.rows.set(position, row);
      }

      this.fill(row, mailbox.items[ids[position]], mailbox);
    }

    if (mailbox) mailbox.scrollTop = this.scroller.scrollTop;
  }

  fill(row, item, mailbox) {
    const find = (name) => row.querySelector(`[data-mb="${name}"]`);
    const conversation = mailbox.conversations ? mailbox.conversationOf(item.id) : null;
    const ticked = mailbox.tickState(item.id);
    const subject = (conversation ? conversation.subject : item.subject) || "(no subject)";

    row.dataset.id = item.id;
    row.id = `mb-row-${item.id}`;
    // In conversation mode the open message may be any of the conversation's.
    const open = conversation ? conversation.members.includes(mailbox.current) : item.id === mailbox.current;
    row.setAttribute("aria-selected", String(open));

    const check = find("row-check");
    check.checked = ticked === "all";
    check.indeterminate = ticked === "some";
    check.setAttribute("aria-label", `Select ${conversation && conversation.count > 1 ? "the conversation" : "the message"} ${subject}`);

    // The search's words, marked where the reader can see them: every plain
    // word anywhere, and a field's own word in that field.
    const { terms, fields } = mailbox.query;
    const fieldTerms = (name) => fields.filter(([field]) => field === name).map(([, value]) => value);

    setMarkedText(find("row-from"), conversation
      ? conversation.people.join(", ") || "Unknown sender"
      : item.fromName || item.fromAddress || "Unknown sender", [...terms, ...fieldTerms("from")]);
    find("row-date").textContent = shortDate(conversation ? conversation.latestDate : item.date);
    setMarkedText(find("row-subject"), subject, [...terms, ...fieldTerms("subject")]);
    find("row-attachment").classList.toggle("hidden", !(conversation ? conversation.attachments : item.maybeAttachments));

    const count = find("row-count");
    const many = conversation && conversation.count > 1;
    count.textContent = many ? String(conversation.count) : "";
    count.classList.toggle("hidden", !many);
    if (many) count.setAttribute("aria-label", `${conversation.count} messages`);
  }

  /** Scrolls just enough to bring a position into view. */
  reveal(position) {
    const height = this.rowHeight;
    const top = position * height;
    if (top < this.scroller.scrollTop) this.scroller.scrollTop = top;
    else if (top + height > this.scroller.scrollTop + this.scroller.clientHeight) {
      this.scroller.scrollTop = top + height - this.scroller.clientHeight;
    }
  }

  /** How many rows fit, for Page Up and Page Down. */
  get pageSize() {
    return Math.max(1, Math.floor(this.scroller.clientHeight / this.rowHeight) - 1);
  }
}
